import { useCallback, useEffect, useRef, useState } from 'react';

import {
  BubbleChatQuestionIcon,
  Clock01Icon,
  ShieldUserIcon,
  User02Icon,
} from '@hugeicons/core-free-icons';
import { getRefereeIntroDescription } from 'helpers/referee-intro';
import { useDocumentTitle } from 'hooks/document-title';
import { useRefereeUploadActions } from 'hooks/upload-actions';
import { useUserAgent } from 'hooks/user-agent';
import i18n from 'i18n';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import AlreadyCompletedForm from 'components/AlreadyCompletedForm/AlreadyCompletedForm';
import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Form } from 'components/Form/Form/Form';
import { FormMessage, MessageType } from 'components/FormComponents';
import LinkedinVerification from 'components/LinkedinVerification/LinkedinVerification';
import { Loading } from 'components/Loading/Loading';
import { Modal } from 'components/Modal/Modal';
import ProgressBarMotion from 'components/Motion/ProgressBarMotion';

import {
  useCreateFormFieldTranslationsMutation,
  useLazyGetUiTranslationsPublicQuery,
} from 'api/forms-api';
import { useLazyGetLinkedinProfileQuery } from 'api/linkedin-api';
import {
  useGetCompanyCheckConfigByTokenQuery,
  useGetRefereeBrandByTokenQuery,
  useGetRefereeByTokenQuery,
  useLazyGetRefereeFormByTokenQuery,
  useLazyVerifyRefereeQuery,
  useSaveResponseMutation,
  useSaveUtmSourceMutation,
} from 'api/referees-api';

import { useQueryParams } from '../../hooks/query-params';
import { CustomButton } from '../CandidateApp/components/CustomButton/CustomButton';
import { CustomLink } from '../CandidateApp/components/CustomLink/CustomLink';
import styles from './RefereeFormSubmission.module.scss';
import { TypeformForm } from './TypeformForm/TypeformForm';

const dotToNested = flat =>
  Object.entries(flat).reduce((acc, [key, val]) => {
    key.split('.').reduce((obj, part, i, arr) => {
      if (i === arr.length - 1) obj[part] = val;
      else obj[part] = obj[part] || {};
      return obj[part];
    }, acc);
    return acc;
  }, {});

export const RefereeFormSubmission = () => {
  useDocumentTitle();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token, answered_on_behalf_of } = useParams();

  const topRef = useRef();

  const { user_agent } = useUserAgent();
  const [utm_source] = useQueryParams(['utm_source']);

  const { data: brand, isLoading: loadingBranding } = useGetRefereeBrandByTokenQuery(token);
  const { data: checkConfig, isLoading: loadingCheckConfig } =
    useGetCompanyCheckConfigByTokenQuery(token);
  const {
    data: referee,
    error: refereeError,
    isLoading: loadingReferee,
  } = useGetRefereeByTokenQuery(token);

  if (refereeError) {
    navigate(`/referee_not_found_error?message=${refereeError?.data?.error?.log}`);
  }

  const { getUpload, signUpload, createUpload, deleteUpload } = useRefereeUploadActions(token);

  const [saveUtmSource] = useSaveUtmSourceMutation();
  const [getRefereeForm, refereeFormResult] = useLazyGetRefereeFormByTokenQuery();
  const [getLinkedinProfile] = useLazyGetLinkedinProfileQuery();
  const [verifyReferee] = useLazyVerifyRefereeQuery();
  const [saveResponse] = useSaveResponseMutation();
  const [createFormFieldTranslations] = useCreateFormFieldTranslationsMutation();
  const [getUiTranslations] = useLazyGetUiTranslationsPublicQuery();

  const [linkedinProfile, setLinkedinProfile] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showIdVerification, setShowIdVerification] = useState(false);
  const [showAlreadyCompleted, setShowAlreadyCompleted] = useState(false);
  const [showIntroduction, setShowIntroduction] = useState(true);
  const [progress, setProgress] = useState(0);

  const originalLanguageRef = useRef(i18n.language);
  const [translatedFields, setTranslatedFields] = useState({});
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);

  // Either one or the other
  const [typeformParams, setTypeformParams] = useState();
  const [form, setForm] = useState();
  const [formParams, setFormParams] = useState({});
  const [uiTranslations, setUiTranslations] = useState(null);
  const [locale, setLocale] = useState('en_GB');
  const [translationsEnabled, setTranslationsEnabled] = useState(false);

  // Save UTM
  useEffect(() => {
    saveUtmSource({ token, utm_source });
  }, []);

  useEffect(() => {
    return () => {
      i18n.changeLanguage(originalLanguageRef.current);
    };
  }, []);

  useEffect(() => {
    if (referee?.completed_at) setShowAlreadyCompleted(true);

    if (!referee?.id || process.env.REACT_APP_ENV !== 'prod') {
      return;
    }

    window.Intercom('shutdown');

    if (!referee?.company?.show_support_widget) {
      return;
    }

    window.Intercom('boot', {
      app_id: 'yh3o8vqn',
      name: referee.name,
      email: referee.email,
      user_id: `referee-${referee.id}-${process.env.REACT_APP_ENV}`,
      user_type: 'referee',
      created_at: moment(referee.created_at, 'DD/MM/YYYY').unix(),
    });
  }, [referee]);

  useEffect(() => {
    if (referee?.form_id) getRefereeForm(token);
  }, [referee?.form_id]);

  const handleLinkedinCode = async code => {
    setLoading(true);
    try {
      const result = await getLinkedinProfile(code).unwrap();
      getForm(result);
    } catch {
      getForm('');
    }
  };

  const getForm = async linkedin_profile => {
    setLoading(true);
    setShowIdVerification(false);
    if (referee?.form_id) {
      setLinkedinProfile(linkedin_profile);
      const { form, params, locale, ui_translations, translations_enabled } =
        await getRefereeForm(token).unwrap();
      setForm(form);
      setFormParams(params);
      setLocale(locale);
      setUiTranslations(ui_translations ?? null);
      setTranslationsEnabled(!!translations_enabled);
      if (form?.translation_language_code && ui_translations) {
        const langCode = form.translation_language_code;
        i18n.addResourceBundle(langCode, 'translation', dotToNested(ui_translations), true, true);
        i18n.changeLanguage(langCode);
        setSelectedLanguageCode(langCode);
      }
    } else {
      const result = await verifyReferee({ token, linkedin_profile, user_agent }).unwrap();
      setTypeformParams(result);
    }
    setLoading(false);
  };

  const handleLanguageChange = useCallback(
    async targetLanguageCode => {
      setSelectedLanguageCode(targetLanguageCode);
      if (!targetLanguageCode) {
        setTranslatedFields({});
        i18n.changeLanguage(originalLanguageRef.current);
        return;
      }
      setIsTranslating(true);
      try {
        const map = await createFormFieldTranslations({
          id: form?.id,
          language_code: targetLanguageCode,
        }).unwrap();
        const texts = {};
        const descs = {};
        const opts = {};
        Object.entries(map).forEach(([fieldId, data]) => {
          if (data.text) texts[fieldId] = data.text;
          if (data.translated_description) descs[fieldId] = data.translated_description;
          if (data.translated_options) opts[fieldId] = data.translated_options;
        });
        setTranslatedFields(
          Object.fromEntries(
            Object.keys(map).map(id => [
              id,
              {
                text: texts[id] ?? null,
                translated_description: descs[id] ?? null,
                translated_options: opts[id] ?? null,
              },
            ]),
          ),
        );
      } catch {}
      try {
        const { data: uiStrings } = await getUiTranslations(targetLanguageCode);
        if (uiStrings) {
          i18n.addResourceBundle(
            targetLanguageCode,
            'translation',
            dotToNested(uiStrings),
            true,
            true,
          );
          i18n.changeLanguage(targetLanguageCode);
        }
      } catch {}
      setIsTranslating(false);
    },
    [form?.id, createFormFieldTranslations, getUiTranslations],
  );

  const closeIntroduction = async () => {
    setShowIntroduction(false);

    if (checkConfig?.disabled_linkedin_verification) {
      getForm('');
    } else {
      setShowIdVerification(true);
    }
  };

  const saveAnswers = async answers => {
    setLoading(true);
    const { error } = await saveResponse({
      token,
      params: {
        user_agent,
        linkedin_profile: linkedinProfile,
        answers,
        ...(answered_on_behalf_of ? { answered_on_behalf_user_id: answered_on_behalf_of } : {}),
      },
    });

    if (error) {
      setError(true);
      setLoading(false);
      if (topRef?.current) {
        topRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      navigate(`/old_referee_completion/${token}`);
    }
  };

  return (
    <div className={styles.root} ref={topRef}>
      <img
        src={brand?.brand_logo || '/assets/images/checkmate/icon-label.svg'}
        className={styles.brandLogo}
        alt=""
      />
      <Loading active={loading || loadingReferee || loadingCheckConfig || loadingBranding} />
      <AnimatedModal visible={showIntroduction} small noClose title="Introduction to Checkmate">
        <p className="u-margin-top--large">
          Checkmate is a secure verification platform designed to simplify the process for employers
          and employees. The privacy and security of your data is our highest priority, and we have
          several systems and processes to keep your information safe and secure.
          <br />
          <br />
          Checkmate is ISO27001 Certified and uses encrypted connections to ensure your privacy and
          confidentiality is maintained throughout this application. We are externally audited
          annually and commission regular independent tests to ensure the security of our system.
          <br />
          <br />
          Checkmate performs several employment checks and verification on behalf of employers; we
          will ask the required questions from the employer so you can provide a reference for the
          candidate.
          <br />
          <br />
          All information you supply is kept strictly confidential and is only accessible by
          authorised staff.
          <br />
          <br />
          Before proceeding, please read our{' '}
          <CustomLink
            brand={brand}
            href="https://www.checkmate.tech/utility/referee-consent-statement">
            Referee Consent Statement
          </CustomLink>
          ,{' '}
          <CustomLink brand={brand} href="https://www.checkmate.tech/utility/privacy-policy">
            Privacy Policy
          </CustomLink>{' '}
          and{' '}
          <CustomLink brand={brand} href="https://www.checkmate.tech/utility/terms-and-conditions">
            Terms and Conditions
          </CustomLink>{' '}
          here. By closing this window, you confirm that you have read, understood, and agreed to
          proceed.
          <br />
          <br />
          If you have any questions or concerns please contact our support team via email at{' '}
          <CustomLink brand={brand} href="mailto:team@checkmate.tech">
            team@checkmate.tech
          </CustomLink>
          .
        </p>
        <CustomButton
          brand={brand}
          onClick={closeIntroduction}
          className="u-margin-top--large"
          dataTestId="referee-intro-close-button">
          Close Window
        </CustomButton>
      </AnimatedModal>

      {showIdVerification && (
        <LinkedinVerification
          brand={brand}
          refereeFirstName={referee?.first_name}
          candidateName={refereeFormResult?.data?.params?.candidateFullName}
          companyName={refereeFormResult?.data?.params?.companyName}
          onLinkedinCode={handleLinkedinCode}
          onSkip={() => getForm('')}
        />
      )}
      <Modal show={showAlreadyCompleted} disableClose>
        <AlreadyCompletedForm />
      </Modal>
      {typeformParams && (
        <TypeformForm
          params={typeformParams}
          onSubmit={() => navigate(`/referee_completion/${token}`)}
        />
      )}
      {form &&
        (() => {
          const candidateName = formParams?.candidateFullName || '';
          const brandColor =
            brand?.brand_active && brand?.color_button ? brand.color_button : '#7a48df';
          const brandIconBg =
            brand?.brand_active && brand?.color_button
              ? brand.color_button
              : 'linear-gradient(180deg, #9e4ff3 0%, #5d33bc 100%)';
          const questionCount = form?.fields?.length || 0;
          const estimatedMinutes = Math.max(1, Math.ceil(questionCount * 0.5));
          return (
            <div className={styles.formLayout} style={{ '--brand-color': brandColor }}>
              <div className={styles.progressTrack}>
                <ProgressBarMotion className={styles.progressBar} dynamicWidth={progress} />
              </div>
              <div className="flex flex-column gap-8 md:w-[700px] w-full py-16 mt-24 px-12">
                {error && (
                  <FormMessage
                    type={MessageType.Error}
                    message="Something went wrong. Please try again. Contact us if the issue persist."
                  />
                )}
                <Form
                  candidateId={referee?.candidate_id}
                  textParams={formParams}
                  form={form}
                  rules={form?.rules}
                  locale={locale}
                  brand={brand}
                  buttonTitle={t('common.submit')}
                  onSubmit={saveAnswers}
                  getCandidateUpload={getUpload}
                  deleteCandidateUpload={deleteUpload}
                  signCandidateUpload={signUpload}
                  createCandidateUpload={createUpload}
                  allowTranslation={translationsEnabled}
                  selectedLanguageCode={selectedLanguageCode}
                  isTranslating={isTranslating}
                  onLanguageChange={handleLanguageChange}
                  translatedFields={translatedFields}
                  onProgressChange={setProgress}
                  detectionEndpoint={'referees/token/' + token + '/language_detection'}
                  fieldRequiredVariantStyle="text"
                  header
                  headerIcon={User02Icon}
                  headerIconBg={brandIconBg}
                  headerSubtitle={t('refereeForm.youveBeenAsked')}
                  headerTitle={candidateName}
                  headerTitleColor={brandColor}
                  headerDescription={getRefereeIntroDescription({
                    form,
                    formParams,
                    estimatedMinutes,
                    t,
                  })}
                  headerBadges={[
                    {
                      icon: BubbleChatQuestionIcon,
                      label: questionCount + ' ' + t('refereeForm.questions'),
                    },
                    {
                      icon: Clock01Icon,
                      label: '~' + estimatedMinutes + ' ' + t('refereeForm.minutes'),
                    },
                    { icon: ShieldUserIcon, label: t('refereeForm.confidential') },
                  ]}
                />
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default RefereeFormSubmission;
